import type { Language, LocaleSettingsClient } from "@bimone/bimtrack-core";
import { I18n, I18nProvider, LocaleSetup } from "@bimone/bimtrack-core";
import { LocaleSettings } from "@bimone/bimtrack-core-micro-app";
import type { ReactNode } from "react";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { QueryClient } from "@tanstack/react-query";
import { useSession } from "shell/session";
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import type { HttpError } from "http-errors";
import type { Resource } from "i18next";
import InvisibleLoading from "@ui/microApp/InvisibleLoading";

const ATTEMPT_COUNT = 3;
const queryClient = new QueryClient();

interface I18nWrapperProps {
  children: ReactNode;
  translationFiles: Resource;
  language?: Language;
}

export default function I18nWrapper({
  translationFiles,
  language,

                                      children,
}: I18nWrapperProps): React.ReactElement {
  const [localI18n, setLocalI18n] = useState<I18n>();
  const [localeSettingsClient, setLocaleSettingsClient] = useState<
    LocaleSettingsClient | undefined
  >(undefined);
  const { user, project } = useSession();

  queryClient.setDefaultOptions({
    queries: {
      enabled: !!project,
      retry: (failureCount, error) =>
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        (error as HttpError).statusCode === 429 && failureCount < ATTEMPT_COUNT,
      retryDelay: 1000,
    },
  });

  useLayoutEffect(() => {
    I18n.createOther(translationFiles, "common", "common").then(
      (newI18n: I18n) => {
        const localeSettings = new LocaleSettings(newI18n);
        localeSettings.setupLocaleSpecifications();
        setLocaleSettingsClient(localeSettings);
        setLocalI18n(newI18n);
      }
    );
  }, [translationFiles]);

  useEffect(() => {
    if(language && localI18n) {
      localI18n.changeLanguage(language);
    }
  }, [language, localI18n]);

  if (!localI18n) {
    return <></>;
  }

  return (
    <>
      <I18nProvider i18n={localI18n}>
        <LocaleSetup
          fallbackComponent={<InvisibleLoading />}
          localeSettings={localeSettingsClient}
          user={user}
        >

          {children}
        </LocaleSetup>
      </I18nProvider>

    </>
  );
}
